/**
 * @fileoverview Page support du portail citoyen.
 *
 * Écran UI-only disponible en mode connecté. Il prépare les demandes
 * d'assistance sans appeler de backend : le formulaire, les motifs et les
 * états affichés servent de contrat visuel pour l'intégration future.
 *
 * « Préparer la demande » simule l'envoi de bout en bout : le ticket est
 * ajouté en tête de la liste « Mes tickets » (state local, perdu au
 * rechargement — comportement attendu d'une maquette sans backend), un
 * toast confirme l'envoi et le formulaire est réinitialisé.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  FaqList,
  FormField,
  InfoNotice,
  Input,
  ReasonTextarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Timeline,
  type TimelineItem,
} from '@gnpc/ui';
import { CircleHelp, FileText, MessageSquareText } from 'lucide-react';

type SupportTopic = 'payment' | 'search' | 'account' | 'other';
type TicketStatus = 'open' | 'waiting' | 'resolved';

interface SupportTicket {
  id: string;
  subject: string;
  reference: string;
  status: TicketStatus;
  statusLabel: string;
  updatedAt: string;
  messages: readonly TimelineItem[];
}

const SUPPORT_TICKETS: readonly SupportTicket[] = [
  {
    id: 'SUP-2026-0142',
    subject: 'Où trouver ma quittance ?',
    reference: 'AB 572 GG · LIQ-921555',
    status: 'open',
    statusLabel: 'En cours',
    updatedAt: "Aujourd'hui, 10:18",
    messages: [
      {
        id: 'sup-0142-created',
        title: 'Demande envoyée',
        timestamp: "Aujourd'hui, 09:41",
        description: 'Justificatif disponible ici ; la quittance se télécharge sur le compte Quipux.',
        tone: 'info',
      },
      {
        id: 'sup-0142-agent',
        title: 'Réponse assistance',
        timestamp: "Aujourd'hui, 10:18",
        description: 'Vérification en cours sur la disponibilité du document.',
        meta: <Badge variant="outline">Réponse reçue</Badge>,
        tone: 'success',
      },
    ],
  },
  {
    id: 'SUP-2026-0138',
    subject: 'Paiement débité non confirmé',
    reference: 'AB 572 GG · 117 000 F CFA',
    status: 'waiting',
    statusLabel: 'À compléter',
    updatedAt: '14 juil. 2026, 16:05',
    messages: [
      {
        id: 'sup-0138-created',
        title: 'Demande envoyée',
        timestamp: '14 juil. 2026, 15:22',
        description: 'Paiement déclaré débité, confirmation encore absente dans le portail.',
        tone: 'info',
      },
      {
        id: 'sup-0138-agent',
        title: 'Complément demandé',
        timestamp: '14 juil. 2026, 16:05',
        description: 'Ajoutez la référence visible sur votre reçu de paiement.',
        meta: <Badge variant="outline">Action requise</Badge>,
        tone: 'warning',
      },
    ],
  },
  {
    id: 'SUP-2026-0129',
    subject: 'Numéro du compte Quipux',
    reference: '+225 07 XX XX XX 42',
    status: 'resolved',
    statusLabel: 'Résolu',
    updatedAt: '12 juil. 2026, 11:30',
    messages: [
      {
        id: 'sup-0129-created',
        title: 'Demande envoyée',
        timestamp: '12 juil. 2026, 10:48',
        description: 'Demande de vérification du numéro utilisé pour recevoir le code SMS.',
        tone: 'info',
      },
      {
        id: 'sup-0129-resolved',
        title: 'Réponse assistance',
        timestamp: '12 juil. 2026, 11:30',
        description: 'Le numéro affiché dans le portail correspond au compte Quipux utilisé.',
        meta: <Badge variant="outline">Clôturé</Badge>,
        tone: 'success',
      },
    ],
  },
] as const;

/**
 * Texte des pastilles en nuance 800 (pas la couleur de statut de base) :
 * sur fond teinté /10, l'ambre et le vert de base descendent sous 3:1 —
 * les nuances 800 garantissent le ratio WCAG AA ≥ 4,5:1 (voir globals.css).
 */
const TICKET_STATUS_CLASSES: Record<TicketStatus, string> = {
  open: 'border-quipux-blue-200 bg-quipux-blue-50 text-quipux-blue-800',
  waiting: 'border-warning/30 bg-warning/10 text-status-pending-800',
  resolved: 'border-status-paid/30 bg-status-paid/10 text-status-paid-800',
};

const DEFAULT_SUPPORT_TICKET = SUPPORT_TICKETS[0]!;

const SUPPORT_FAQ_ITEMS = [
  {
    question: 'Que dois-je préparer avant de contacter le support ?',
    answer:
      "Préparez la plaque, le numéro LIQ si vous l'avez, la date du paiement et le justificatif affiché dans l'historique.",
    defaultOpen: true,
  },
  {
    question: "Puis-je demander de l'aide si aucune contravention n'est trouvée ?",
    answer:
      "Oui. Vérifiez d'abord le format de la plaque, puis décrivez le problème si le résultat semble incohérent.",
  },
  {
    question: 'Le support peut-il modifier mon paiement ?',
    answer:
      "Non depuis cette page. L'assistance aide à comprendre le statut et à orienter la demande vers le bon traitement.",
  },
  {
    question: 'Dois-je partager des données personnelles ?',
    answer:
      'Non. Limitez la demande à la plaque, aux références de paiement et aux informations visibles dans le portail.',
  },
] as const;

const TOPIC_LABELS: Record<SupportTopic, string> = {
  payment: 'Paiement ou quittance',
  search: 'Recherche par plaque',
  account: 'Compte Quipux',
  other: 'Autre demande',
};

export function SupportPage(): JSX.Element {
  const [topic, setTopic] = useState<SupportTopic>('payment');
  const [reference, setReference] = useState('AB 572 GG');
  const [reason, setReason] = useState(
    'Je souhaite obtenir une aide sur le statut de mon paiement.',
  );
  // Liste vivante des tickets : les mocks servent d'historique initial et
  // chaque demande préparée vient s'ajouter EN TÊTE (tri anté-chronologique,
  // comme le fera le backend).
  const [tickets, setTickets] = useState<readonly SupportTicket[]>(SUPPORT_TICKETS);
  const [selectedTicketId, setSelectedTicketId] = useState(DEFAULT_SUPPORT_TICKET.id);
  const [ticketSheetOpen, setTicketSheetOpen] = useState(false);
  const canPrepare = reason.trim().length >= 20 && reference.trim().length >= 3;
  const selectedTicket =
    tickets.find((ticket) => ticket.id === selectedTicketId) ?? DEFAULT_SUPPORT_TICKET;

  const openTicket = (ticket: SupportTicket) => {
    setSelectedTicketId(ticket.id);
    setTicketSheetOpen(true);
  };

  /**
   * Remet le formulaire à son état de départ : motif et référence par
   * défaut, description VIDÉE (le compteur du textarea repasse à 0).
   */
  const resetForm = () => {
    setTopic('payment');
    setReference('AB 572 GG');
    setReason('');
  };

  /**
   * Simule l'envoi de la demande : construit un ticket « En cours » à
   * partir du formulaire, l'insère en tête de « Mes tickets », confirme
   * par un toast puis réinitialise le formulaire. La numérotation continue
   * celle des mocks (SUP-2026-0143, 0144…) pour rester plausible.
   */
  const prepareRequest = () => {
    const createdCount = tickets.length - SUPPORT_TICKETS.length;
    const ticketId = `SUP-2026-${String(143 + createdCount).padStart(4, '0')}`;
    const newTicket: SupportTicket = {
      id: ticketId,
      subject: TOPIC_LABELS[topic],
      reference: reference.trim(),
      status: 'open',
      statusLabel: 'En cours',
      updatedAt: "À l'instant",
      messages: [
        {
          id: `${ticketId.toLowerCase()}-created`,
          title: 'Demande envoyée',
          timestamp: "À l'instant",
          description: reason.trim(),
          tone: 'info',
        },
      ],
    };
    setTickets((current) => [newTicket, ...current]);
    toast.success('Demande envoyée', {
      description: `Le ticket ${ticketId} apparaît dans « Mes tickets » avec le statut « En cours ».`,
    });
    resetForm();
  };

  return (
    <div className="bg-slate-50">
      <section className="portal-animated-hero bg-quipux-navy-950">
        <div className="relative z-10 mx-auto max-w-5xl px-4 pb-32 pt-12 md:pb-36 md:pt-16">
          <header className="max-w-2xl space-y-2">
            <p className="text-quipux-orange-400 text-sm font-medium">Support</p>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Support et assistance
            </h1>
            <p className="text-sm leading-6 text-white/70">
              Préparez une demande, suivez vos tickets et retrouvez les échanges liés à votre
              parcours.
            </p>
          </header>
        </div>
      </section>

      <div className="relative z-10 mx-auto -mt-20 grid max-w-5xl gap-6 px-4 pb-10">
        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div id="demande-assistance" className="border-border bg-card rounded-lg border p-5">
            <div className="flex items-start gap-3">
              <div className="text-quipux-orange-600 bg-quipux-orange-50 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md">
                <FileText className="size-5" />
              </div>
              <div>
                <h2 className="text-quipux-navy-950 text-lg font-semibold">Préparer une demande</h2>
                <p className="text-muted-foreground mt-1 text-sm leading-6">
                  Décrivez le besoin avec les références visibles dans le portail pour faciliter le
                  traitement de votre demande.
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4">
              {/* `controlId` + `id` sur le trigger : le Root Radix ne rend
                * aucun nœud focusable, le label doit pointer sur le bouton
                * réel — sinon il cible le select natif caché de Radix
                * (violation W3C) et le trigger reste sans nom accessible
                * (violation axe button-name). */}
              <FormField label="Motif" required controlId="support-topic">
                <Select value={topic} onValueChange={(value) => setTopic(value as SupportTopic)}>
                  <SelectTrigger
                    id="support-topic"
                    aria-label="Motif de la demande"
                    className="h-11 w-full"
                  >
                    <SelectValue placeholder="Choisir un motif" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment">Paiement ou quittance</SelectItem>
                    <SelectItem value="search">Recherche par plaque</SelectItem>
                    <SelectItem value="account">Compte Quipux</SelectItem>
                    <SelectItem value="other">Autre demande</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              <FormField
                label="Référence"
                tooltip="Plaque, numéro LIQ ou référence de paiement visible dans le portail."
              >
                <Input
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  placeholder="AB 572 GG, LIQ-921555..."
                />
              </FormField>

              <FormField label="Description" required>
                <ReasonTextarea
                  value={reason}
                  minReasonLength={20}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Décrivez le problème rencontré."
                />
              </FormField>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  disabled={!canPrepare}
                  onClick={prepareRequest}
                  className="sm:w-auto"
                >
                  Préparer la demande
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Réinitialiser
                </Button>
              </div>
            </div>
          </div>

          {/* `<section>` et non `<aside>` : un landmark `complementary`
            * imbriqué dans `<main>` viole axe
            * « landmark-complementary-is-top-level ». */}
          <section aria-label="Informations utiles" className="space-y-4 lg:pt-0">
            <InfoNotice title="Informations utiles">
              Mentionnez uniquement les éléments visibles dans le portail : plaque, LIQ, montant,
              date de paiement ou statut affiché.
            </InfoNotice>

            <div className="border-border bg-card rounded-lg border p-4">
              <h2 className="text-quipux-navy-950 text-sm font-semibold">Canaux d'assistance</h2>
              <dl className="mt-3 grid gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Disponibilité</dt>
                  <dd className="text-quipux-navy-950 font-medium">Jours ouvrés</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Priorité</dt>
                  <dd className="text-quipux-navy-950 font-medium">Paiement débité ou bloqué</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Donnée sensible</dt>
                  <dd className="text-quipux-navy-950 font-medium">
                    Ne partagez jamais de code SMS
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        </section>

        <section className="border-border bg-card rounded-lg border p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="text-quipux-blue-700 bg-quipux-blue-50 mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md">
                <MessageSquareText className="size-5" />
              </div>
              <div>
                <h2 className="text-quipux-navy-950 text-lg font-semibold">Mes tickets</h2>
                <p className="text-muted-foreground mt-1 text-sm leading-6">
                  Suivez les demandes envoyées et les échanges associés.
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-background">
              {tickets.length} tickets
            </Badge>
          </div>

          <div className="border-border mt-5 overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent">
                  <TableHead>Ticket</TableHead>
                  <TableHead>Référence</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="min-w-40">Mis à jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    tabIndex={0}
                    role="button"
                    aria-label={`Ouvrir le ticket ${ticket.id}`}
                    onClick={() => openTicket(ticket)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      openTicket(ticket);
                    }}
                    className="hover:bg-quipux-orange-50/30 focus-visible:bg-quipux-blue-50 cursor-pointer outline-none transition-colors"
                  >
                    <TableCell>
                      <div className="min-w-56">
                        <p className="text-quipux-navy-950 font-semibold">{ticket.subject}</p>
                        <p className="text-muted-foreground mt-1 font-mono text-xs">{ticket.id}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground min-w-48">
                      {ticket.reference}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TICKET_STATUS_CLASSES[ticket.status]}>
                        {ticket.statusLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground min-w-40 text-sm">
                      {ticket.updatedAt}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <Sheet open={ticketSheetOpen} onOpenChange={setTicketSheetOpen}>
          <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
            <SheetHeader className="border-border border-b p-5 pr-12">
              <SheetTitle>Détail du ticket</SheetTitle>
              <SheetDescription>
                {selectedTicket.id} · {selectedTicket.reference}
              </SheetDescription>
            </SheetHeader>

            <div className="grid gap-5 p-5">
              <div className="border-border rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-quipux-navy-950 text-base font-semibold">
                      {selectedTicket.subject}
                    </p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Mis à jour {selectedTicket.updatedAt}
                    </p>
                  </div>
                  <Badge variant="outline" className={TICKET_STATUS_CLASSES[selectedTicket.status]}>
                    {selectedTicket.statusLabel}
                  </Badge>
                </div>
              </div>

              <section className="space-y-3">
                <h3 className="text-quipux-navy-950 text-sm font-semibold">Échanges</h3>
                <Timeline
                  items={selectedTicket.messages}
                  compact
                  ariaLabel={`Échanges du ticket ${selectedTicket.id}`}
                />
              </section>
            </div>

            <SheetFooter className="border-border border-t p-5">
              <SheetClose asChild>
                <Button type="button" variant="outline">
                  Fermer
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <CircleHelp className="text-quipux-blue-700 size-5" />
            <h2 className="text-quipux-navy-950 text-lg font-semibold">Questions fréquentes</h2>
          </div>
          <FaqList items={SUPPORT_FAQ_ITEMS} />
        </section>
      </div>
    </div>
  );
}
